import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api.js';
import {
  navigate,
  scheduleVersion,
  bumpScheduleVersion,
  showToast,
  friendlyError,
  household,
} from '../store.js';
import { __ } from '../i18n.js';
import {
  formatDayHeading,
  formatDayShort,
  formatWeekRange,
  formatOccurrenceTime,
  scheduleIconName,
} from '../lib/schedule.js';
import { useVisibleRefresh } from '../lib/refresh.js';
import Header from '../components/Header.jsx';
import Icon from '../components/Icon.jsx';
import Modal from '../components/Modal.jsx';

/**
 * HH-6: the week overview (plan §4.3).
 *
 * Layout, resolving the brief's "Week grid on mobile" default: one
 * vertical day list, which at the 1024px breakpoint becomes a 7-column
 * grid through CSS alone (see .etm-week-days in theme.css) — the same
 * markup either way, so there is no second render path to keep in sync.
 * Today's card is highlighted and scrolled into view on load; the other
 * days stay readable rather than collapsed, because a week you have to
 * expand day by day is not an overview.
 *
 * Every date decision is the server's (ETM_Schedule::week() → the pure
 * ETM_Schedule_Logic): this screen sends an integer week offset and
 * renders what comes back, including which day is `is_today`. It never
 * computes an occurrence, a week start, or a weekday itself.
 */
export default function WeekScreen() {
  const [offset, setOffset] = useState(0);
  const [week, setWeek] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null); // the tapped occurrence
  const version = scheduleVersion.value; // reading .value here subscribes this component to bumps
  // `is_today` is decided server-side when the payload is built, so a PWA
  // left open across midnight must re-ask rather than keep rendering
  // yesterday's week under today's heading.
  const visibleTick = useVisibleRefresh();
  const todayRef = useRef(null);
  const scrolledFor = useRef(null);

  // One request per offset/refresh. Guarded by a per-effect `cancelled`
  // flag plus a request sequence, so a slow earlier response can never
  // overwrite a fresher one — the WP-2 UpcomingPanel lesson.
  const latestRequest = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const requestId = ++latestRequest.current;
    setLoading(true);

    Promise.all([api.schedule.week(offset), api.people.list().catch(() => ({ people: [] }))])
      .then(([weekRes, peopleRes]) => {
        if (cancelled || requestId !== latestRequest.current) return;
        setWeek(weekRes);
        setPeople(peopleRes.people || []);
        setError(false);
      })
      .catch(() => {
        if (cancelled || requestId !== latestRequest.current) return;
        // Drop the stale grid rather than keep last week's days on screen
        // under a navigator label that now claims a different week.
        setWeek(null);
        setError(true);
      })
      .finally(() => {
        if (cancelled || requestId !== latestRequest.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [offset, version, visibleTick]);

  // Scroll today into view once per week that contains it — not on every
  // render, or a skip-toggle would yank the page back mid-interaction.
  useEffect(() => {
    if (!week || !todayRef.current) return;
    if (scrolledFor.current === week.week_start) return;
    scrolledFor.current = week.week_start;
    todayRef.current.scrollIntoView({ block: 'center', behavior: 'auto' });
  }, [week]);

  function personFor(personId) {
    if (!personId) return null;
    return people.find((person) => Number(person.id) === Number(personId)) || null;
  }

  async function toggleSkip(occurrence) {
    const nextSkipped = !occurrence.skipped;
    setSelected(null);
    try {
      await api.schedule.setSkipped(occurrence.item_id, occurrence.date, nextSkipped);
      showToast(
        nextSkipped ? __('Deze keer overgeslagen.', 'etm') : __('Staat weer op de planning.', 'etm'),
        'success'
      );
      bumpScheduleVersion();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    }
  }

  function openForm(params) {
    setSelected(null);
    navigate('scheduleForm', params);
  }

  /**
   * The day the FAB pre-selects: today when the displayed week contains
   * it, otherwise that week's Monday — "add something to the week I am
   * looking at" is the only reading that makes sense while paging.
   */
  function defaultAddDay() {
    if (!week) return {};
    const day = week.days.find((candidate) => candidate.is_today) || week.days[0];
    return { date: day.date, weekdayBit: day.weekday_bit };
  }

  const isEmptyWeek = week && week.days.every((day) => day.occurrences.length === 0);

  return (
    <>
      <Header title={__('Weekritme', 'etm')} />

      <div class="etm-week-nav">
        <button
          type="button"
          class="etm-icon-btn"
          aria-label={__('Vorige week', 'etm')}
          onClick={() => setOffset(offset - 1)}
        >
          <Icon name="chevron_left" size={22} />
        </button>
        <button type="button" class="etm-week-nav__label" onClick={() => setOffset(0)}>
          <span class="etm-week-nav__title">
            {offset === 0 ? __('Deze week', 'etm') : formatWeekRange(week?.week_start, week?.week_end)}
          </span>
          {offset === 0 && week ? (
            <span class="etm-week-nav__sub">{formatWeekRange(week.week_start, week.week_end)}</span>
          ) : null}
        </button>
        <button
          type="button"
          class="etm-icon-btn"
          aria-label={__('Volgende week', 'etm')}
          onClick={() => setOffset(offset + 1)}
        >
          <Icon name="chevron_right" size={22} />
        </button>
      </div>

      {error ? (
        <p class="etm-field-hint">{__('De week kon niet worden geladen.', 'etm')}</p>
      ) : null}

      {loading && !week ? <p class="etm-field-hint">{__('Laden…', 'etm')}</p> : null}

      {week ? (
        <div class="etm-week-days">
          {week.days.map((day) => {
            const short = formatDayShort(day.date);
            return (
              <section
                key={day.date}
                class={`etm-week-day${day.is_today ? ' etm-week-day--today' : ''}`}
                ref={day.is_today ? todayRef : null}
              >
                <header class="etm-week-day__header">
                  <span class="etm-week-day__name">{formatDayHeading(day.date)}</span>
                  <span class="etm-week-day__short" aria-hidden="true">
                    <span class="etm-week-day__short-weekday">{short.weekday}</span>
                    <span class="etm-week-day__short-day">{short.day}</span>
                  </span>
                  <button
                    type="button"
                    class="etm-week-day__add"
                    aria-label={__('Item toevoegen', 'etm')}
                    onClick={() => openForm({ date: day.date, weekdayBit: day.weekday_bit })}
                  >
                    <Icon name="add" size={18} />
                  </button>
                </header>

                {day.occurrences.length === 0 ? (
                  <p class="etm-week-day__empty">{__('Niets gepland', 'etm')}</p>
                ) : (
                  <ul class="etm-week-chips">
                    {day.occurrences.map((occurrence) => {
                      const person = personFor(occurrence.person_id);
                      const time = formatOccurrenceTime(occurrence);
                      return (
                        <li key={`${occurrence.item_id}-${occurrence.date}`}>
                          <button
                            type="button"
                            class={`etm-week-chip etm-week-chip--${person ? person.color_role : 'neutral'}${
                              occurrence.skipped ? ' etm-week-chip--skipped' : ''
                            }`}
                            onClick={() => setSelected(occurrence)}
                          >
                            <Icon name={scheduleIconName(occurrence.icon)} size={18} />
                            <span class="etm-week-chip__body">
                              <span class="etm-week-chip__title">{occurrence.title}</span>
                              <span class="etm-week-chip__meta">
                                {time || __('Hele dag', 'etm')}
                                {person ? ` · ${person.name}` : ''}
                              </span>
                            </span>
                            {occurrence.skipped ? <Icon name="event_busy" size={16} /> : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      ) : null}

      {week && isEmptyWeek && !loading ? (
        <div class="etm-empty-state">
          <Icon name="calendar_month" size={40} />
          <p class="etm-screen-subtitle">
            {household.value
              ? __('Nog niets in het weekritme. Voeg school, sport of een afspraak toe.', 'etm')
              : __('Nog niets in het weekritme. Voeg je vaste momenten toe.', 'etm')}
          </p>
          <button type="button" class="etm-btn etm-btn-primary" onClick={() => openForm(defaultAddDay())}>
            {__('Item toevoegen', 'etm')}
          </button>
        </div>
      ) : null}

      {/* The week's own add affordance. Deliberately NOT app.jsx's
          FAB_VISIBLE set: that FAB opens WP-6's task quick-add sheet, and
          a schedule item is not a task — the whole premise of plan §4.3 is
          that time-bound commitments never enter the task engine. Same
          .etm-fab styling and position, so it reads as one pattern, and it
          renders inside .etm-app so WP-2's translateZ(0) containing block
          keeps it in the left pane on desktop for free. */}
      {week ? (
        <button
          type="button"
          class="etm-fab"
          aria-label={__('Item toevoegen', 'etm')}
          onClick={() => openForm(defaultAddDay())}
        >
          <Icon name="add" size={24} />
        </button>
      ) : null}

      {selected ? (
        <Modal title={selected.title} onClose={() => setSelected(null)}>
          <p class="etm-field-hint">
            {formatDayHeading(selected.date)}
            {formatOccurrenceTime(selected) ? ` · ${formatOccurrenceTime(selected)}` : ''}
          </p>

          <button
            type="button"
            class="etm-btn etm-btn-primary"
            onClick={() => openForm({ itemId: selected.item_id })}
          >
            <Icon name="edit" size={20} />
            {__('Bewerken', 'etm')}
          </button>

          {/* Skipping one occurrence never edits the series (plan §4.3):
              it writes a skip-exception row, and the same button undoes it.
              A `once` item has no series to leave intact, so its only
              honest action is deleting it from the form. */}
          {selected.kind === 'weekly' ? (
            <button type="button" class="etm-btn etm-btn-secondary" onClick={() => toggleSkip(selected)}>
              <Icon name={selected.skipped ? 'undo' : 'event_busy'} size={20} />
              {selected.skipped ? __('Toch laten staan', 'etm') : __('Deze keer overslaan', 'etm')}
            </button>
          ) : null}

          <button type="button" class="etm-btn etm-btn-ghost" onClick={() => setSelected(null)}>
            {__('Annuleren', 'etm')}
          </button>
        </Modal>
      ) : null}
    </>
  );
}
